// Worker Service for BookAPT Mobile App
// Handles worker management and persistence

import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';

export interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  specialties: string;
  businessId: string;
  createdAt: string;
  isActive?: boolean;
  isOwner?: boolean;
}

class WorkerService {
  private readonly WORKERS_KEY_PREFIX = 'workers_';
  
  /**
   * Get all workers for a business
   * For testing: Loads from local storage first, then syncs from Firestore if available
   */
  async getWorkers(businessId: string): Promise<Worker[]> {
    try {
      let loadedWorkers: Worker[] = [];
      
      // For testing: Load from local storage first (faster, avoids Firestore reads)
      const key = `${this.WORKERS_KEY_PREFIX}${businessId}`;
      const workersJson = await SimpleStorage.getItem(key);
      
      if (workersJson) {
        loadedWorkers = JSON.parse(workersJson);
        console.log(`📋 WorkerService: Loaded ${loadedWorkers.length} workers from local storage`);
      }
      
      // Then try to sync from Firestore if available (for cross-device sync)
      if (Platform.OS === 'web') {
        try {
          // Use getFirestoreFunctions() so doc/collection/getDoc share the same
          // firebase/firestore module instance as getWebDb() (require vs dynamic
          // import can duplicate the SDK and trigger "Expected first argument to collection()").
          const { getFirestoreFunctions } = require('./unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const firestoreMod = getFirestoreFunctions();
          if (!firestoreMod) {
            return loadedWorkers;
          }
          const { doc, getDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          // Validate Firestore instance. v9 modular instances expose
          // _delegate, _settings, or type — accept any of them.
          const isValidFirestore = !!db && typeof db === 'object' && (
            '_delegate' in db || '_settings' in db || 'type' in db
          );
          if (!isValidFirestore) {
            // No Firestore available on this client — fall back to local cache silently.
            return loadedWorkers;
          }
          
          if (businessId) {
            // Use proper Firestore path: businesses/{businessId}/workers/all
            const workersDocRef = doc(db, 'businesses', businessId, 'workers', 'all');
            const workersDoc = await getDoc(workersDocRef);
            
            if (workersDoc.exists()) {
              const data = workersDoc.data();
              const firestoreWorkers = data.workers || [];
              
              // Only update if Firestore has data and it's different
              if (firestoreWorkers.length > 0) {
                loadedWorkers = firestoreWorkers;
                console.log(`✅ WorkerService: Synced ${loadedWorkers.length} workers from Firestore`);
                
                // Sync to local storage for offline access
                await SimpleStorage.setItem(key, JSON.stringify(loadedWorkers));
              }
            }
          }
        } catch (firestoreError: any) {
          // Common cause: duplicate firebase/firestore copies in the bundle make
          // `collection()` reject a Firestore instance built by a sibling copy.
          // The local cache is the source of truth, so this is non-fatal.
          const msg = firestoreError?.message || '';
          if (msg.includes('Expected first argument to collection()')) {
            console.warn('ℹ️  WorkerService: Firestore client mismatch detected, using local cache.');
          } else {
            console.warn('ℹ️  WorkerService: Firestore read unavailable, using local cache:', msg);
          }
        }
      } else {
        // Mobile: load from Firestore first (same DB as web), then fallback to local
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          initializeFirebase();
          const getFirestore = getMobileFirestore?.();
          if (getFirestore && typeof getFirestore === 'function') {
            const db = getFirestore();
            if (db && typeof db.collection === 'function' && businessId) {
              const workersDoc = await db
                .collection('businesses')
                .doc(businessId)
                .collection('workers')
                .doc('all')
                .get();
              if (workersDoc.exists) {
                const data = workersDoc.data() || {};
                const firestoreWorkers = data.workers || [];
                if (firestoreWorkers.length > 0) {
                  loadedWorkers = firestoreWorkers;
                  console.log(`✅ WorkerService: Synced ${loadedWorkers.length} workers from Firestore (mobile)`);
                  await SimpleStorage.setItem(key, JSON.stringify(loadedWorkers));
                }
              }
            }
          }
        } catch (mobileFirestoreError: any) {
          console.warn('⚠️  WorkerService: Could not sync from Firestore on mobile (using local):', mobileFirestoreError?.message);
        }
      }
      
      return loadedWorkers;
    } catch (error) {
      console.error('WorkerService: Error getting workers:', error);
      return [];
    }
  }
  
  /**
   * Save workers for a business
   */
  async saveWorkers(businessId: string, workers: Worker[]): Promise<void> {
    try {
      // Save to local storage first (for offline access and testing)
      const key = `${this.WORKERS_KEY_PREFIX}${businessId}`;
      await SimpleStorage.setItem(key, JSON.stringify(workers));
      console.log(`📋 WorkerService: Saved ${workers.length} workers to local storage`);
      
      // Also save to Firestore (for cross-browser/device access)
      if (Platform.OS === 'web') {
        try {
          const { getFirestoreFunctions } = require('./unifiedAuthService');
          const { getApps, initializeApp } = require('firebase/app');
          const { firebaseConfig } = require('../config/firebaseConfig');
          const firestoreMod = getFirestoreFunctions();
          if (!firestoreMod) {
            return;
          }
          const { doc, setDoc, getFirestore } = firestoreMod;
          const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
          const db = getFirestore(app);
          
          const isValidFirestore = !!db && typeof db === 'object' && (
            '_delegate' in db || '_settings' in db || 'type' in db
          );
          if (!isValidFirestore) {
            // Local save already succeeded; quietly skip remote sync.
            return;
          }
          
          if (businessId) {
            // Use proper Firestore path: businesses/{businessId}/workers/all
            const workersDocRef = doc(db, 'businesses', businessId, 'workers', 'all');
            await setDoc(workersDocRef, {
              workers: workers,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ WorkerService: Synced ${workers.length} workers to Firestore`);
          }
        } catch (firestoreError: any) {
          const msg = firestoreError?.message || '';
          if (msg.includes('Expected first argument to collection()')) {
            console.warn('ℹ️  WorkerService: Firestore client mismatch detected, saved locally only.');
          } else {
            console.warn('ℹ️  WorkerService: Firestore save unavailable, saved locally only:', msg);
          }
          // Don't throw - local save succeeded
        }
      } else {
        // Mobile: save to Firestore (same path as web)
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          initializeFirebase();
          const getFirestore = getMobileFirestore?.();
          if (getFirestore && typeof getFirestore === 'function') {
            const db = getFirestore();
            if (db && typeof db.collection === 'function' && businessId) {
              await db
                .collection('businesses')
                .doc(businessId)
                .collection('workers')
                .doc('all')
                .set({
                  workers,
                  updatedAt: new Date().toISOString(),
                });
              console.log(`✅ WorkerService: Synced ${workers.length} workers to Firestore (mobile)`);
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  WorkerService: Could not sync to Firestore on mobile (saved locally):', firestoreError?.message);
        }
      }
    } catch (error) {
      console.error('WorkerService: Error saving workers:', error);
      throw error;
    }
  }
  
  /**
   * Add a new worker
   */
  async addWorker(
    businessId: string, 
    workerData: Omit<Worker, 'id' | 'businessId' | 'createdAt'>
  ): Promise<Worker> {
    try {
      const workers = await this.getWorkers(businessId);
      
      const newWorker: Worker = {
        id: `worker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...workerData,
        businessId,
        createdAt: new Date().toISOString(),
        isActive: true
      };
      
      workers.push(newWorker);
      await this.saveWorkers(businessId, workers);
      
      console.log(`✅ WorkerService: Added worker ${newWorker.name} to business ${businessId}`);
      return newWorker;
    } catch (error) {
      console.error('WorkerService: Error adding worker:', error);
      throw error;
    }
  }
  
  /**
   * Update a worker
   */
  async updateWorker(businessId: string, workerId: string, updates: Partial<Worker>): Promise<Worker | null> {
    try {
      const workers = await this.getWorkers(businessId);
      const workerIndex = workers.findIndex(w => w.id === workerId);
      
      if (workerIndex === -1) {
        console.error(`WorkerService: Worker ${workerId} not found`);
        return null;
      }
      
      workers[workerIndex] = {
        ...workers[workerIndex],
        ...updates
      };
      
      await this.saveWorkers(businessId, workers);
      
      console.log(`✅ WorkerService: Updated worker ${workerId}`);
      return workers[workerIndex];
    } catch (error) {
      console.error('WorkerService: Error updating worker:', error);
      throw error;
    }
  }
  
  /**
   * Delete a worker
   */
  async deleteWorker(businessId: string, workerId: string): Promise<void> {
    try {
      const workers = await this.getWorkers(businessId);
      const filtered = workers.filter(w => w.id !== workerId);
      
      await this.saveWorkers(businessId, filtered);
      
      console.log(`✅ WorkerService: Deleted worker ${workerId} from business ${businessId}`);
    } catch (error) {
      console.error('WorkerService: Error deleting worker:', error);
      throw error;
    }
  }
  
  /**
   * Get available workers for a specific time slot
   */
  async getAvailableWorkersForSlot(
    businessId: string,
    date: string,
    time: string,
    allBookings: any[]
  ): Promise<Worker[]> {
    try {
      const workers = await this.getWorkers(businessId);
      
      // Filter workers who don't have a booking at this time
      const availableWorkers = workers.filter(worker => {
        const isBooked = allBookings.some(booking => 
          booking.businessId === businessId &&
          booking.appointmentDate === date &&
          booking.appointmentTime === time &&
          booking.workerId === worker.id &&
          booking.status !== 'cancelled'
        );
        
        return !isBooked && worker.isActive !== false;
      });
      
      return availableWorkers;
    } catch (error) {
      console.error('WorkerService: Error getting available workers:', error);
      return [];
    }
  }
  
  /**
   * Initialize default workers for a business
   * For solo businesses, creates only the owner as a worker
   * For larger businesses, returns empty array - business owners must add their own workers
   * (No hardcoded workers for production)
   */
  async initializeDefaultWorkers(businessId: string, businessName: string, isSoloBusiness: boolean = false): Promise<void> {
    try {
      const existingWorkers = await this.getWorkers(businessId);
      
      if (existingWorkers.length > 0) {
        console.log(`WorkerService: Business ${businessId} already has ${existingWorkers.length} workers`);
        return;
      }
      
      let defaultWorkers: Worker[];
      
      if (isSoloBusiness) {
        // For solo businesses, create only the owner as a worker
        defaultWorkers = [
          {
            id: `owner_${businessId}`,
            name: `${businessName} Owner`,
            phone: '(555) 000-0000',
            email: `owner@${businessName.toLowerCase().replace(/\s+/g, '')}.com`,
            specialties: 'Owner, All Services',
            businessId,
            createdAt: new Date().toISOString(),
            isActive: true,
            isOwner: true
          }
        ];
        console.log(`✅ WorkerService: Initialized solo business owner as worker for ${businessName}`);
        await this.saveWorkers(businessId, defaultWorkers);
      } else {
        // For larger businesses, return empty array - business owners must add their own workers
        // No hardcoded workers for production
        defaultWorkers = [];
        console.log(`✅ WorkerService: No default workers created for ${businessName}. Business owner should add workers manually.`);
        // Don't save empty array - let business owners add workers through the UI
      }
    } catch (error) {
      console.error('WorkerService: Error initializing default workers:', error);
    }
  }
}

export default new WorkerService();

